
import MarketKpiCard from "@/components/marketAnalysis/MarketKpiCard";
import { initMarketAnalysisKpiCardsConfiguration, kpiBoxCardTypes } from '@/constants/marketAnalysis';
import MarketAnalysisService from "@/service//MarketAnalysisService";
import ChartService from '@/service/ChartService';

export default {
    name: 'MarketAnalysisKpis',

    components: {
        MarketKpiCard
    },

    data() {
        return {
            kpisData: {
                [kpiBoxCardTypes.fearAndGreed]: {},
                [kpiBoxCardTypes.vix]: {},
                [kpiBoxCardTypes.consumerSentiment]: {},
                [kpiBoxCardTypes.usInflationRate]: {},
                [kpiBoxCardTypes.fedInterestRate]: {},
                [kpiBoxCardTypes.initialClaims]: {},
            },
            isInitializing: {
                [kpiBoxCardTypes.fearAndGreed]: true,
                [kpiBoxCardTypes.vix]: true,
                [kpiBoxCardTypes.consumerSentiment]: true,
                [kpiBoxCardTypes.usInflationRate]: true,
                [kpiBoxCardTypes.fedInterestRate]: true,
                [kpiBoxCardTypes.initialClaims]: true,
            },
            isLoading: {
                [kpiBoxCardTypes.fearAndGreed]: true,
                [kpiBoxCardTypes.vix]: true,
                [kpiBoxCardTypes.consumerSentiment]: true,
                [kpiBoxCardTypes.usInflationRate]: true,
                [kpiBoxCardTypes.fedInterestRate]: true,
                [kpiBoxCardTypes.initialClaims]: true,
            },
            cardsConfiguration: [],
        }
    },

    computed: {
        isLoggedIn() {
            return !!this.$store.state.account.user
        },
        interval() {
            return this.$store.getters["preferences/getMarketAnalysisChartInterval"]
        },
    },

    watch: {
        interval() {
            this.fetchVIXData()
            this.fetchConsumerSentimentData();
            this.fetchUSInflationsrateData();
            this.fetchFedInterestRateData();
            this.fetchInitialClaimsData();
        },
        isLoggedIn: {
            handler() {
                this.fetchCardsConfiguration()
            },
            immediate: true
        },
    },

    created() {
        this.chartService = new ChartService(this.$axios);
        this.marketAnalysisService = new MarketAnalysisService(this.$axios);
        this.fetchVIXData();
        this.fetchFearAndGreedData();
        this.fetchConsumerSentimentData();
        this.fetchUSInflationsrateData();
        this.fetchFedInterestRateData();
        this.fetchInitialClaimsData();
        this.fetchCardsConfiguration();
    },

    methods: {
        async fetchVIXData() {
            try {
                this.isLoading[kpiBoxCardTypes.vix] = true

                const res = await this.chartService.get({
                    source: 'share',
                    sourceIdentifier: 'US12497K1007',
                    interval: this.interval,
                    comparisons: [],
                    nonCancelable: true,
                }, this.$t)

                this.kpisData[kpiBoxCardTypes.vix] = res.graphs[0]
            } catch (err) {
                this.kpisData[kpiBoxCardTypes.vix] = {}
            } finally {
                this.isInitializing[kpiBoxCardTypes.vix] = false
                this.isLoading[kpiBoxCardTypes.vix] = false
            }
        },

        async handleCardTypeChanged(payload) {
            const { cardType, position } = payload
            const originConfigurations = [...this.cardsConfiguration]
            const tempConfigurations = [...this.cardsConfiguration]
            tempConfigurations[position] = cardType
            this.cardsConfiguration = tempConfigurations

            try {
                await this.marketAnalysisService.updateKpiCardsConfiguration(this.cardsConfiguration.join(','))
            } catch (error) {
                this.cardsConfiguration = originConfigurations
            }
        },

        async fetchCardsConfiguration() {
            try {
                if (!this.isLoggedIn) {
                    this.cardsConfiguration = initMarketAnalysisKpiCardsConfiguration
                    return
                }
                const res = await this.marketAnalysisService.getKpiCardsConfiguration()
                const configs = res.value.split(',')
                if (configs.length < initMarketAnalysisKpiCardsConfiguration.length) {
                    this.cardsConfiguration = configs.concat(initMarketAnalysisKpiCardsConfiguration.slice(configs.length))
                } else {
                    this.cardsConfiguration = configs.slice(0, initMarketAnalysisKpiCardsConfiguration.length)
                }
            } catch (err) {
                this.cardsConfiguration = initMarketAnalysisKpiCardsConfiguration
            }
        },

        async fetchFearAndGreedData() {
            try {
                this.isLoading[kpiBoxCardTypes.fearAndGreed] = true

                const res = await this.marketAnalysisService.getFearAndGreed()
                this.kpisData[kpiBoxCardTypes.fearAndGreed] = res
            } catch (err) {
                this.kpisData[kpiBoxCardTypes.fearAndGreed] = {}
            } finally {
                this.isInitializing[kpiBoxCardTypes.fearAndGreed] = false
                this.isLoading[kpiBoxCardTypes.fearAndGreed] = false
            }
        },

        async fetchConsumerSentimentData() {
            try {
                this.isLoading[kpiBoxCardTypes.consumerSentiment] = true

                const res = await this.chartService.get({
                    source: 'share',
                    sourceIdentifier: 'EI00CONSENTI',
                    interval: this.interval,
                    comparisons: [],
                    nonCancelable: true,
                }, this.$t)

                this.kpisData[kpiBoxCardTypes.consumerSentiment] = res.graphs[0]
            } catch (err) {
                this.kpisData[kpiBoxCardTypes.consumerSentiment] = {}
            } finally {
                this.isInitializing[kpiBoxCardTypes.consumerSentiment] = false
                this.isLoading[kpiBoxCardTypes.consumerSentiment] = false
            }
        },

        async fetchUSInflationsrateData() {
            try {
                this.isLoading[kpiBoxCardTypes.usInflationRate] = true

                const res = await this.chartService.get({
                    source: 'share',
                    sourceIdentifier: 'EI00INFLRATE',
                    interval: this.interval,
                    comparisons: [],
                    nonCancelable: true,
                }, this.$t)

                this.kpisData[kpiBoxCardTypes.usInflationRate] = res.graphs[0]
            } catch (err) {
                this.kpisData[kpiBoxCardTypes.usInflationRate] = {}
            } finally {
                this.isInitializing[kpiBoxCardTypes.usInflationRate] = false
                this.isLoading[kpiBoxCardTypes.usInflationRate] = false
            }
        },

        async fetchFedInterestRateData() {
            try {
                this.isLoading[kpiBoxCardTypes.fedInterestRate] = true

                const res = await this.chartService.get({
                    source: 'share',
                    sourceIdentifier: 'EI00FEDFUNDS',
                    interval: this.interval,
                    comparisons: [],
                    nonCancelable: true,
                }, this.$t)

                this.kpisData[kpiBoxCardTypes.fedInterestRate] = res.graphs[0]
            } catch (err) {
                this.kpisData[kpiBoxCardTypes.fedInterestRate] = {}
            } finally {
                this.isInitializing[kpiBoxCardTypes.fedInterestRate] = false
                this.isLoading[kpiBoxCardTypes.fedInterestRate] = false
            }
        },

        async fetchInitialClaimsData() {
            try {
                this.isLoading[kpiBoxCardTypes.initialClaims] = true

                const res = await this.chartService.get({
                    source: 'share',
                    sourceIdentifier: 'EI00INITCLMS',
                    interval: this.interval,
                    comparisons: [],
                    nonCancelable: true,
                }, this.$t)

                this.kpisData[kpiBoxCardTypes.initialClaims] = res.graphs[0]
            } catch (err) {
                this.kpisData[kpiBoxCardTypes.initialClaims] = {}
            } finally {
                this.isInitializing[kpiBoxCardTypes.initialClaims] = false
                this.isLoading[kpiBoxCardTypes.initialClaims] = false
            }
        },
    },
}
